import { useState } from 'react';
import {
  addToArray,
  arrayMove,
  filterOutValue,
  mergeWithoutDuplicates,
  removeFromArray,
  replaceInArray,
} from '@witmetrics/utils/arrays';

export function useArray<T = string>(
  initialValue?: T[],
  allowDuplicates = false
) {
  const [items, setItems] = useState<T[]>(initialValue || []);

  const clear = () => setItems([]);

  const reset = () => setItems(initialValue || []);

  const addItem = (item: T) => {
    return setItems((arr) => addToArray(arr, item, allowDuplicates));
  };

  const addItems = (items: T[]) => {
    return setItems((arr) =>
      allowDuplicates ? [...arr, ...items] : mergeWithoutDuplicates(arr, items)
    );
  };

  const appendItem = (item: T) => {
    return setItems((arr) => addToArray(arr, item, allowDuplicates, 0));
  };

  const replaceItem = (index: number, newItem: T) => {
    return setItems((arr) => replaceInArray(arr, index, newItem));
  };

  const moveItem = (oldIndex: number, newIndex: number) => {
    return setItems((arr) => arrayMove(arr, oldIndex, newIndex));
  };

  const removeItem = (index: number) => {
    return setItems((arr) => removeFromArray(arr, index));
  };

  const filterItem = (value: T) => {
    return setItems((arr) => filterOutValue(arr, value));
  };

  return [
    items,
    setItems,
    {
      clear,
      reset,
      addItem,
      addItems,
      appendItem,
      replaceItem,
      moveItem,
      removeItem,
      filterItem,
    },
  ] as const;
}
